/**
 * Emissions Data Export Page
 *
 * Export emissions data and calculation details as CSV.
 */

import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { generateUrl } from '@routes/util';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { downloadBlob } from '@utils/downloadBlob';
import { emissionsTrackerRoutes } from '../routes';
import { useLazyExportEmissionsQuery } from '../api/emissions';

// Get default date range (current fiscal year)
const getDefaultDateRange = () => {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: `${year}-07-01`,
    end: `${year + 1}-06-30`,
  };
};

const inputClass =
  'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring';

export const EmissionsDataExportPage = () => {
  const history = useHistory();
  const { initiativeId } = useParams<{ initiativeId: string }>();

  const defaultDates = getDefaultDateRange();
  const [periodStart, setPeriodStart] = useState(defaultDates.start);
  const [periodEnd, setPeriodEnd] = useState(defaultDates.end);
  const [includeChildren, setIncludeChildren] = useState(false);

  const [triggerExport, { isFetching: isLoading }] = useLazyExportEmissionsQuery();

  const handleBack = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ADMIN, { initiativeId }));
  };

  const handleExport = async () => {
    try {
      const blob = await triggerExport({
        initiativeId,
        periodStart,
        periodEnd,
        includeChildren,
      }).unwrap();

      downloadBlob(blob, `emissions-${periodStart}-to-${periodEnd}.csv`);
      generateToast({ color: 'success', message: 'Export downloaded successfully' });
    } catch (error) {
      toastFromError({ error, title: 'Export failed' });
    }
  };

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='tw:flex tw:items-center tw:gap-3 tw:mb-6'>
        <Button variant='ghost' size='icon' onClick={handleBack}>
          <ArrowLeft className='tw:size-4' />
        </Button>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>Data Export</h4>
          <p className='tw:text-sm tw:text-muted-foreground'>Export emissions data and calculation details</p>
        </div>
      </div>

      {/* Period Selection */}
      <Card className='tw:mb-4'>
        <CardContent className='tw:p-4'>
          <h5 className='tw:font-semibold tw:mb-4'>Reporting Period</h5>
          <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4'>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>Start Date</label>
              <input
                type='date'
                className={inputClass}
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <label className='tw:block tw:text-sm tw:font-medium tw:mb-1.5'>End Date</label>
              <input
                type='date'
                className={inputClass}
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className='tw:flex tw:items-end tw:pb-0.5'>
              <label className='tw:flex tw:items-center tw:gap-2 tw:cursor-pointer'>
                <input
                  type='checkbox'
                  className='tw:h-4 tw:w-4 tw:rounded tw:border-input'
                  checked={includeChildren}
                  onChange={(e) => setIncludeChildren(e.target.checked)}
                />
                <span className='tw:text-sm'>Include subsidiaries</span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Contents */}
      <Card className='tw:mb-6'>
        <CardContent className='tw:p-4'>
          <h5 className='tw:font-semibold tw:mb-2'>Export Contents</h5>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-3'>The CSV export includes:</p>
          <ul className='tw:text-sm tw:text-muted-foreground tw:space-y-1 tw:list-disc tw:pl-4'>
            <li>Activity data (scope, type, quantity, unit, fuel type, region)</li>
            <li>Period information (start and end dates)</li>
            <li>Data quality and source</li>
            <li>Calculated emissions (CO2e in kg)</li>
            <li>Emission factor details (source, value, GWP methodology)</li>
            <li>Calculation timestamp</li>
          </ul>
        </CardContent>
      </Card>

      {/* Export Button */}
      <div className='tw:flex tw:justify-end tw:gap-2'>
        <Button variant='outline' onClick={handleBack}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          disabled={isLoading || !periodStart || !periodEnd}
          className='tw:bg-emerald-800 tw:hover:bg-emerald-900 tw:text-white tw:border-0'
        >
          {isLoading ? (
            <>
              <Loader2 className='tw:size-4 tw:animate-spin' />
              Exporting...
            </>
          ) : (
            <>
              <Download className='tw:size-4' />
              Export CSV
            </>
          )}
        </Button>
      </div>
    </Dashboard>
  );
};
